// Components the Student profile migration needed that src/ui doesn't have yet.
// They follow src/ui conventions (ds- class names, --ds-* tokens) so they can be
// promoted into src/ui as-is. Keep this list in sync with README.md.
import React from 'react';
import { Modal } from 'reactstrap';
import { Badge, StatusBadge } from '../../../../../ui';
import type { BadgeTone } from '../../../../../ui';
import { cx } from '../../../../../ui/components/cx';
import './ui-pending.scss';

/* ------------------------------------------------------------------------ */
/* TextArea: multi-line ds-input.                                           */
/* ------------------------------------------------------------------------ */
export const TextArea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, rows = 3, ...rest }, ref) => (
    <textarea ref={ref} rows={rows} className={cx('ds-input', 'sp-textarea', className)} {...rest} />
  ),
);
TextArea.displayName = 'TextArea';

/* ------------------------------------------------------------------------ */
/* Stat / StatGroup: a number with a label (class counts, balances).        */
/* ------------------------------------------------------------------------ */
export type StatTone = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

export const Stat: React.FC<{ label: React.ReactNode; value: React.ReactNode; tone?: StatTone; className?: string }> = ({
  label,
  value,
  tone = 'default',
  className,
}) => (
  <div className={cx('sp-stat', tone !== 'default' && `sp-stat--${tone}`, className)}>
    <span className="sp-stat__value">{value ?? '—'}</span>
    <span className="sp-stat__label">{label}</span>
  </div>
);

/** Row of Stats on a muted tile. `title` is an optional small heading. */
export const StatGroup: React.FC<{ title?: React.ReactNode; children: React.ReactNode; className?: string }> = ({
  title,
  children,
  className,
}) => (
  <div className={cx('sp-stat-group', className)}>
    {title && <div className="sp-stat-group__title">{title}</div>}
    <div className="sp-stat-group__items">{children}</div>
  </div>
);

/* ------------------------------------------------------------------------ */
/* Callout: inline notice (info, warning…) inside a panel. Not for toasts.  */
/* ------------------------------------------------------------------------ */
export type CalloutTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'primary';

export const Callout: React.FC<{
  tone?: CalloutTone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ tone = 'info', title, children, action, className }) => (
  <div className={cx('sp-callout', `sp-callout--${tone}`, className)} role={tone === 'danger' ? 'alert' : undefined}>
    <div className="sp-callout__content">
      {title && <div className="sp-callout__title">{title}</div>}
      {children && <div className="sp-callout__body">{children}</div>}
    </div>
    {action && <div className="sp-callout__action">{action}</div>}
  </div>
);

/* ------------------------------------------------------------------------ */
/* Dialog: reactstrap Modal with ds-dialog styling and a fixed layout.      */
/* ------------------------------------------------------------------------ */
export const Dialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Footer buttons, right aligned. Put the primary action last. */
  actions?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
  className?: string;
  /** Passed to reactstrap Modal (e.g. 'static' to block outside clicks). */
  backdrop?: boolean | 'static';
  centered?: boolean;
}> = ({ isOpen, onClose, title, subtitle, actions, size = 'md', children, className, backdrop, centered = true }) => (
  <Modal
    isOpen={isOpen}
    toggle={onClose}
    centered={centered}
    size={size === 'md' ? undefined : size}
    backdrop={backdrop}
    className={cx('ds-dialog', 'ds-dialog--panel', className)}
    returnFocusAfterClose
  >
    <div className="ds-dialog__header">
      <div>
        <h2 className="ds-dialog__title">{title}</h2>
        {subtitle && <p className="ds-dialog__subtitle">{subtitle}</p>}
      </div>
      <button type="button" className="ds-dialog__close" aria-label="Close" onClick={onClose}>
        ×
      </button>
    </div>
    <div className="ds-dialog__content">{children}</div>
    {actions && <div className="ds-dialog__actions">{actions}</div>}
  </Modal>
);

/* ------------------------------------------------------------------------ */
/* Tabs: segmented in-panel tabs (not page navigation).                     */
/* ------------------------------------------------------------------------ */
export interface TabItem {
  id: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export const Tabs: React.FC<{
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  className?: string;
}> = ({ items, value, onChange, ariaLabel, className }) => (
  <div className={cx('sp-tabs', className)} role="tablist" aria-label={ariaLabel}>
    {items.map((t) => (
      <button
        key={t.id}
        type="button"
        role="tab"
        aria-selected={value === t.id}
        disabled={t.disabled}
        className={cx('sp-tabs__tab', value === t.id && 'sp-tabs__tab--active')}
        onClick={() => onChange(t.id)}
      >
        {t.label}
      </button>
    ))}
  </div>
);

/* ------------------------------------------------------------------------ */
/* NavList: vertical list of section buttons (the profile tab sidebar).     */
/* ------------------------------------------------------------------------ */
export interface NavListItem {
  id: string;
  label: React.ReactNode;
  disabled?: boolean;
  title?: string;
}

export const NavList: React.FC<{
  items: NavListItem[];
  activeId: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
}> = ({ items, activeId, onSelect, ariaLabel }) => (
  <nav aria-label={ariaLabel}>
    <ul className="ds-navlist">
      {items.map((item) => {
        const active = item.id.toLowerCase() === activeId.toLowerCase();
        return (
          <li key={item.id}>
            <button
              type="button"
              className={cx('ds-navlist__item', active && 'ds-navlist__item--active')}
              aria-current={active ? 'page' : undefined}
              disabled={item.disabled}
              title={item.title}
              onClick={() => onSelect(item.id)}
            >
              {item.label}
            </button>
          </li>
        );
      })}
    </ul>
  </nav>
);

/* ------------------------------------------------------------------------ */
/* StudentStatusBadge: StatusBadge plus statuses src/ui's map lacks.        */
/* Promote EXTRA_STATUS_TONES into src/ui/components/Badge.tsx STATUS_TONES. */
/* ------------------------------------------------------------------------ */
export const EXTRA_STATUS_TONES: Record<string, BadgeTone> = {
  captured: 'success',
  paid: 'success',
  succesful: 'success', // backend spelling
  attended: 'success',
  present: 'success',
  booked: 'info',
  upcoming: 'info',
  rescheduled: 'info',
  created: 'info',
  open: 'info',
  refunded: 'neutral',
  partially_refunded: 'warning',
  inactive: 'neutral',
  missed: 'danger',
  absent: 'danger',
  'no show': 'danger',
  noshow: 'danger',
  deducted: 'danger',
  unscheduled: 'warning',
  initiated: 'warning',
  'in progress': 'info',
  // Payment link / order statuses (new-paymentlink, course-extension)
  payment_success: 'success',
  payment_success_class_created: 'success',
  payment_success_class_created_error: 'danger',
  order_created: 'info',
  issued: 'info',
};

export const StudentStatusBadge: React.FC<{ status?: string | null; label?: React.ReactNode }> = ({ status, label }) => {
  const key = String(status ?? '').trim().toLowerCase();
  const tone = EXTRA_STATUS_TONES[key];
  if (tone) return <Badge tone={tone}>{label ?? key}</Badge>;
  return <StatusBadge status={status} label={label} />;
};

/* ------------------------------------------------------------------------ */
/* InlineActions: right-aligned row of buttons under a form or card.        */
/* ------------------------------------------------------------------------ */
export const Actions: React.FC<{ children: React.ReactNode; align?: 'start' | 'end' | 'between'; className?: string }> = ({
  children,
  align = 'end',
  className,
}) => <div className={cx('ds-actions', `ds-actions--${align}`, className)}>{children}</div>;

/** Responsive grid for form Fields (auto-fills columns of at least `min` px). */
export const FormGrid: React.FC<{ children: React.ReactNode; min?: 180 | 240 | 320; className?: string }> = ({
  children,
  min = 240,
  className,
}) => <div className={cx('ds-form-grid', `ds-form-grid--${min}`, className)}>{children}</div>;
